import type { PublicKey } from '@solana/web3.js';
import { CP_SWAP_PROGRAM_ID } from '../constants/programs.js';
import { deriveAmmConfigPda, deriveAmmPoolStatePda, sortMintPair } from '../pda/cp-swap.js';
import { deriveMarketPda, deriveOutcomeMintPda } from '../pda/derivations.js';
import type { MarketAccount } from './types.js';

export interface GraduatedMarketPoolBinding {
  poolState: PublicKey;
  outcomeMint: PublicKey;
  outcomeIndex: number;
  quoteMint: PublicKey;
}

/** Current Markets keep the winner binding on the permanent Market account. */
export function graduatedMarketPoolBinding(
  marketAddress: PublicKey,
  market: MarketAccount,
): GraduatedMarketPoolBinding {
  validateMarketAddress(marketAddress, market);
  if (!hasVariant(market.state, 'graduated') || !hasVariant(market.migrationStatus, 'migrated')) {
    throw new Error('Market has not migrated to a cp-swap pool');
  }
  const outcomeIndex = market.winningOutcome;
  if (
    !Number.isInteger(outcomeIndex) ||
    outcomeIndex < 0 ||
    outcomeIndex >= market.nOutcomes ||
    market.outcomes[outcomeIndex] === undefined ||
    (market.preparedOutcomesBitmap & (1 << outcomeIndex)) === 0
  ) {
    throw new Error('Market has no prepared winning outcome');
  }
  const [outcomeMint] = deriveOutcomeMintPda(marketAddress, outcomeIndex);
  const [mint0, mint1] = sortMintPair(market.quoteMint, outcomeMint);
  const poolState = deriveAmmPoolStatePda(
    deriveAmmConfigPda(CP_SWAP_PROGRAM_ID),
    mint0,
    mint1,
    CP_SWAP_PROGRAM_ID,
  );
  return { poolState, outcomeMint, outcomeIndex, quoteMint: market.quoteMint };
}

export function validateMarketAddress(marketAddress: PublicKey, market: MarketAccount): void {
  const [expectedAddress, bump] = deriveMarketPda(market.creator, market.marketNonce);
  if (!marketAddress.equals(expectedAddress) || market.marketBump !== bump) {
    throw new Error('Market account does not match its canonical PDA');
  }
  if (!Number.isInteger(market.nOutcomes) || market.nOutcomes < 2 || market.nOutcomes > 10) {
    throw new Error('Market outcome count is invalid');
  }
}

export function hasVariant(value: string | object, variant: string): boolean {
  return value === variant || (typeof value === 'object' && variant in value);
}
